import { Check, Redo2, Undo2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ReactNode } from 'react';

function PickaxeFilledIcon({ className }: { className?: string }) {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='currentColor' stroke='none' className={className}>
      <path d='m14 13-8.381 8.38a1 1 0 0 1-3.001-3L11 9.999' />
      <path d='M15.973 4.027A13 13 0 0 0 5.902 2.373c-1.398.342-1.092 2.158.277 2.601a19.9 19.9 0 0 1 5.822 3.024' />
      <path d='M16.001 11.999a19.9 19.9 0 0 1 3.024 5.824c.444 1.369 2.26 1.676 2.603.278A13 13 0 0 0 20 8.069' />
      <path d='M18.352 3.352a1.205 1.205 0 0 0-1.704 0l-5.296 5.296a1.205 1.205 0 0 0 0 1.704l2.296 2.296a1.205 1.205 0 0 0 1.704 0l5.296-5.296a1.205 1.205 0 0 0 0-1.704z' />
    </svg>
  );
}

/**
 * CARET POSITION, AS A CHARACTER OFFSET INTO THE WHOLE FIELD.
 *
 * An `<input>` keeps its selection across a window focus change; a `contentEditable` does not —
 * the browser hands focus back to the element with the caret collapsed at the start. So typing a
 * title, alt-tabbing to look something up and alt-tabbing back appended the next keystroke to the
 * FRONT of the title. Nothing in the app did that; it is simply what the element does, so the
 * position has to be saved on the way out and put back on the way in.
 *
 * An offset in characters rather than a `Range`, because a Range holds node references and the
 * nodes may not survive the round trip.
 */
function caretOffsetIn(el: HTMLElement): number | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!el.contains(range.endContainer)) return null;
  const upToCaret = range.cloneRange();
  upToCaret.selectNodeContents(el);
  upToCaret.setEnd(range.endContainer, range.endOffset);
  return upToCaret.toString().length;
}

function placeCaretAt(el: HTMLElement, offset: number): void {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let remaining = offset;
  let target: Text | null = null;
  let node = walker.nextNode();
  while (node) {
    const text = node as Text;
    const length = text.textContent?.length ?? 0;
    if (remaining <= length) { target = text; break; }
    remaining -= length;
    node = walker.nextNode();
  }
  const range = document.createRange();
  if (target) range.setStart(target, Math.min(remaining, target.textContent?.length ?? 0));
  else { range.selectNodeContents(el); range.collapse(false); }
  range.collapse(true);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
}

interface TaskTitlebarProps {
  taskTitle: string;
  saving: boolean;
  /** True after a save attempt fails for any reason other than a 409 conflict (which reloads the
   * page directly instead) — cleared at the start of the next attempt. */
  saveFailed: boolean;
  isDirty: boolean;
  canUndo: boolean;
  canRedo: boolean;
  undoLabel: string | null;
  redoLabel: string | null;
  onTitleSave: (title: string) => void;
  onSave: () => void;
  onUndo: () => void;
  onRedo: () => void;
  /** Optional slot rendered between the title and the undo/redo group (phone landscape). */
  /** Centred over the editing column — the task stepper from `sm` up. */
  centerSlot?: ReactNode;
  rightSlot?: ReactNode;
  /** Collapses the saved state to a bare checkmark (no "Saved" label) to save width. */
  isPhone?: boolean;
  /** Another tab holds write access (see useTaskTakeOver) — title becomes non-editable and
   * Undo/Redo/Save all become no-ops (the page's own handlers already refuse to run too; this
   * just keeps the controls visually consistent with that). */
  readOnly?: boolean;
}

export default function TaskTitlebar({
  taskTitle,
  saving,
  saveFailed,
  isDirty,
  canUndo,
  canRedo,
  undoLabel,
  redoLabel,
  onTitleSave,
  onSave,
  onUndo,
  onRedo,
  rightSlot,
  centerSlot,
  isPhone,
  readOnly,
}: TaskTitlebarProps) {
  const { t } = useTranslation();
  const titleRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const titleWrapRef = useRef<HTMLDivElement>(null);
  const stepperRef = useRef<HTMLDivElement>(null);
  const lastSaved = useRef(taskTitle);
  const isFocusedRef = useRef(false);
  /** Where the caret was when the WINDOW lost focus, so returning to it puts it back. */
  const caretOnWindowBlur = useRef<number | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isEmpty, setIsEmpty] = useState(!taskTitle);

  // Initialise DOM content on mount
  useEffect(() => {
    if (titleRef.current) titleRef.current.textContent = taskTitle;
  }, []);

  /*
   * Sync prop changes when not being edited, and ONLY when they are actually changes.
   *
   * Writing `textContent` destroys the selection inside a contentEditable and drops the caret at
   * the start. Alt-tabbing away fires `blur`, which saves; the parent echoes the same string back;
   * this effect rewrote the DOM with text identical to what was already there; and alt-tabbing
   * back returned the teacher to a caret at character zero, several words from where they left
   * off. The guard makes the no-op write a no-op.
   */
  useEffect(() => {
    lastSaved.current = taskTitle;
    setIsEmpty(!taskTitle);
    if (titleRef.current && !isFocusedRef.current && titleRef.current.textContent !== taskTitle) {
      titleRef.current.textContent = taskTitle;
    }
  }, [taskTitle]);

  /*
   * Saving and restoring the caret across an alt-tab.
   *
   * On the WINDOW's events, not the element's: the element also blurs for an ordinary click
   * elsewhere on the page, where restoring would be wrong.
   *
   * RESTORED REPEATEDLY FOR A SHORT WINDOW, rather than once. The browser sets its own caret when
   * it hands focus back, and the order in which that happens relative to the window's `focus`
   * event is not something to rely on — a single-shot restore won it sometimes and lost it
   * sometimes, which is exactly how this was reported the second time: fine if you had been in
   * the field a while, broken if you clicked in and alt-tabbed straight out. Rather than guess at
   * the ordering a third time, the restore simply outlasts it, and stops the moment the teacher
   * does anything that means they have chosen a position themselves.
   */
  useEffect(() => {
    let frame = 0;
    const onWindowBlur = () => {
      const el = titleRef.current;
      if (el && document.activeElement === el) caretOnWindowBlur.current = caretOffsetIn(el);
    };
    const onWindowFocus = () => {
      if (caretOnWindowBlur.current == null) return;
      const deadline = performance.now() + 250;
      const tick = () => {
        const el = titleRef.current;
        const want = caretOnWindowBlur.current;
        // `want` is null once a keystroke or a click has said otherwise.
        if (want == null || !el || performance.now() >= deadline) {
          caretOnWindowBlur.current = null;
          frame = 0;
          return;
        }
        if (document.activeElement === el && caretOffsetIn(el) !== want) placeCaretAt(el, want);
        frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    };
    window.addEventListener('blur', onWindowBlur);
    window.addEventListener('focus', onWindowFocus);
    return () => {
      window.removeEventListener('blur', onWindowBlur);
      window.removeEventListener('focus', onWindowFocus);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  /*
   * HOW WIDE THE TITLE MAY GROW: measured, not guessed.
   *
   * The stepper is absolutely positioned over the CANVAS centre, so the room left for the title
   * is a function of the row's width, the right panel's width and the stepper's own — and the
   * last of those is not a constant, because "10/12" is wider than "1/3". Static caps were tried
   * and were wrong in both directions, measured in a browser: `11rem` left 14px unused at 1280,
   * and `max-w-md` ran 130px INTO the stepper at 1536 and 58px at 1680. A teacher editing a long
   * title typed underneath it.
   *
   * Measuring costs one observer and is exact at every width, with every task count, and through
   * the block library collapsing — which moves the row's left edge and therefore both of them.
   *
   * No media query needed: the stepper is `hidden xl:block`, so below `xl` it measures zero and
   * the cap is simply removed, which is the behaviour that breakpoint was expressing anyway.
   */
  useEffect(() => {
    const fit = () => {
      const wrap = titleWrapRef.current;
      if (!wrap) return;
      const stepper = stepperRef.current?.getBoundingClientRect();
      if (!stepper || stepper.width === 0) {
        if (wrap.style.maxWidth !== '') wrap.style.maxWidth = '';
        return;
      }
      // 16px so the two never touch; a floor so a narrow window yields a usable field rather than
      // a sliver, since at that point the row has bigger problems than this.
      const room = Math.max(120, Math.round(stepper.left - wrap.getBoundingClientRect().left - 16));
      const next = `${room}px`;
      if (wrap.style.maxWidth !== next) wrap.style.maxWidth = next;
    };
    fit();
    const observer = new ResizeObserver(fit);
    if (rowRef.current) observer.observe(rowRef.current);
    if (stepperRef.current) observer.observe(stepperRef.current);
    window.addEventListener('resize', fit);
    return () => { observer.disconnect(); window.removeEventListener('resize', fit); };
  }, [centerSlot, isFocused]);

  function handleFocus() {
    isFocusedRef.current = true;
    setIsFocused(true);
  }

  function handleBlur() {
    isFocusedRef.current = false;
    setIsFocused(false);
    const text = titleRef.current?.textContent?.trim() ?? '';
    setIsEmpty(!text);
    if (!text) {
      if (titleRef.current) titleRef.current.textContent = lastSaved.current;
      setIsEmpty(!lastSaved.current);
      return;
    }
    if (text !== lastSaved.current) {
      lastSaved.current = text;
      onTitleSave(text);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    // Typing is the teacher taking over: whatever was being restored is now their business.
    caretOnWindowBlur.current = null;
    if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
    if (e.key === 'Escape') {
      if (titleRef.current) titleRef.current.textContent = lastSaved.current;
      e.currentTarget.blur();
    }
  }

  // Strip HTML on paste — title is plain text only
  function handlePaste(e: React.ClipboardEvent<HTMLDivElement>) {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain').replace(/\n/g, ' ');
    document.execCommand('insertText', false, text);
  }

  function handleInput() {
    setIsEmpty(!(titleRef.current?.textContent?.trim()));
  }

  return (
    <div ref={rowRef} className='relative flex min-h-14 shrink-0 items-center gap-3 border-b border-ws-border bg-ws-surface px-3 py-2'>

      {/*
        The task stepper, centred over the EDITING COLUMN — which is what this row spans, so
        centring here needs no knowledge of the block library's width (and that width changes
        when the library collapses, so measuring it would have made the control move).

        Absolutely positioned on purpose: this row's flex layout is carefully balanced (a title
        that grows on focus, `ml-auto` on the history cluster, a landscape `rightSlot`), and a
        third flex zone would have disturbed all three. `pointer-events-none` on the wrapper so
        the empty space either side of the buttons does not swallow clicks meant for the title.

        CENTRED ON THE CANVAS, NOT ON THIS ROW. The row also spans the block-editor panel, and
        centring on it put the stepper visibly right of where the eye sits — the content a
        teacher is reading is the canvas. `-ml-48` is half of the panel's `w-96`, which is a
        constant worth relying on only because of the breakpoint below.

        `xl` AND UP ONLY, and the threshold was computed rather than picked. Centring on the
        canvas pulls the stepper LEFT, toward the title, so the room it needs is
        `titleStart + minTitle + gap < canvasCentre - stepperWidth/2` — which solves to about
        1150px. Below that the canvas is simply too narrow to hold a centred control and a
        readable title: at 1024 it is 384px wide and leaves the title ~70px.

        Two other things break below `lg` anyway: `needsSubbar` is live, so the panel animates
        between `w-0` and `w-80`/`w-96` and this offset would be a lie that moves; and the row
        has ~48px spare at both 375px and 768px against the ~98px the stepper needs, which is
        how it came to overlap the title AND the history cluster at 768. Under `xl` the nav bar
        carries it instead.
      */}
      {centerSlot && (
        <div ref={stepperRef} className='pointer-events-none absolute left-1/2 top-1/2 -ml-48 hidden -translate-x-1/2 -translate-y-1/2 xl:block'>
          <div className='pointer-events-auto'>{centerSlot}</div>
        </div>
      )}

      <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded bg-ws-primary'>
        <PickaxeFilledIcon aria-hidden='true' className='h-6 w-6 text-white' />
      </div>

      {/* Capped from `xl`, where the stepper appears centred on the canvas to the right of this:
          an uncapped title is content-sized, and a long one ran straight under it. The cap grows
          at `2xl` because the gap between the title and the canvas centre does too.

          THE CAP APPLIES WHILE EDITING TOO, which it did not. Focus swapped the whole cap out for
          `max-w-xl`, so the field a teacher was typing into grew past the gap and slid under the
          stepper — the one moment the text most needs to be readable. Below `xl` the stepper is
          not rendered at all, so there `flex-1 max-w-xl` still applies and editing gets the room.
          Above it the field wraps instead of widening, which is why it is `whitespace-normal`. */}
      {/* `maxWidth` is set imperatively by the fit effect above, which measures the gap to the
          stepper rather than guessing it with a breakpoint. `max-w-xl` remains the ceiling while
          editing for the widths where no stepper is rendered and nothing is measured. */}
      <div ref={titleWrapRef} className={`relative min-w-0 ${isFocused ? 'flex-1 max-w-xl' : ''}`}>
        {isEmpty && !isFocused && (
          <span className='absolute inset-0 text-lg font-semibold text-ws-muted px-2 py-0.5 pointer-events-none select-none'>
            {t('task.titlePlaceholder')}
          </span>
        )}
        <div
          ref={titleRef}
          contentEditable={!readOnly}
          suppressContentEditableWarning
          role='textbox'
          aria-label={t('task.titlePlaceholder')}
          aria-placeholder={t('task.titlePlaceholder')}
          aria-multiline='true'
          onFocus={handleFocus}
          onBlur={handleBlur}
          // Clicking is choosing a position, so forget the one saved for the alt-tab.
          onMouseDown={() => { caretOnWindowBlur.current = null; }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onInput={handleInput}
          className={`rounded-md bg-black/[0.04] dark:bg-white/[0.06] text-lg font-semibold text-ws-text
                      border-b-2 border-transparent transition-colors
                      hover:bg-black/[0.07] dark:hover:bg-white/[0.10]
                      focus:bg-black/[0.04] dark:focus:bg-white/[0.06] focus:outline-none
                      px-2 py-0.5 break-words
                      ${isFocused ? 'whitespace-normal' : 'whitespace-nowrap overflow-hidden text-ellipsis'}`}
        />
      </div>

      {/*
        History sits with the TITLE; mode and review sit at the far edge.

        Only where there is a `rightSlot` to make room for, which today means phone landscape —
        everywhere else the slot is empty and `ml-auto` stays on this cluster, so nothing moves.

        Landscape had undo/redo/save pinned to the right edge with Variables, Flow and Preview
        crowded in beside the title, and it read as a pile rather than as two groups. Undo, Redo and
        Save act on the DOCUMENT, so they belong next to its name; Variables, Flow and Preview
        change what you are looking at, so they belong at the end. It also gives the bar one stable
        right-hand anchor across modes: the primary action is Preview while editing and Exit while
        previewing, in the same place, with the accessibility controls beside it.
      */}
      <div className={`flex shrink-0 items-center gap-0.5 ${rightSlot ? '' : 'ml-auto'}`}>
        <button
          onClick={onUndo}
          disabled={!canUndo || readOnly}
          className='flex h-8 w-8 items-center justify-center rounded-md hover:bg-ws-hover disabled:opacity-30 disabled:pointer-events-none'
          aria-label={undoLabel ? t('nav.undoLabel', { label: undoLabel }) : t('nav.undo')}
          title={undoLabel ? t('nav.undoLabel', { label: undoLabel }) : t('nav.undo')}
        >
          <Undo2 className='h-4 w-4 text-ws-text' />
        </button>
        <button
          onClick={onRedo}
          disabled={!canRedo || readOnly}
          className='flex h-8 w-8 items-center justify-center rounded-md hover:bg-ws-hover disabled:opacity-30 disabled:pointer-events-none'
          aria-label={redoLabel ? t('nav.redoLabel', { label: redoLabel }) : t('nav.redo')}
          title={redoLabel ? t('nav.redoLabel', { label: redoLabel }) : t('nav.redo')}
        >
          <Redo2 className='h-4 w-4 text-ws-text' />
        </button>

        {/* Same button drives both manual save and the autosave status: "Save" while dirty
         * (enabled — autosave fires on its own ~1s later, but a teacher can force it sooner),
         * a checkmark + "Saved" once autosave/manual-save lands (dimmed via disabled:opacity,
         * same treatment this button always used — not a different inert style), and a
         * red-tinted "Save" again if the last attempt failed (still enabled, since isDirty
         * stays true until a save succeeds — clicking retries). A fixed min-width (sized for the
         * longest label, "Save failed") keeps the button's own width constant across all of these
         * states — without it, every state change shifts this button's width and, since Undo/Redo
         * sit to its left in the same flex row, shifts THEIR on-screen position too, which is
         * exactly what breaks repeated fast Undo/Redo clicks landing on a moving target. Skipped
         * only in the phone-collapsed idle state, which intentionally narrows to a bare checkmark
         * to save space. */}
        <button
          onClick={onSave}
          disabled={saving || !isDirty || readOnly}
          aria-label={saving ? t('task.titleBar.saving') : saveFailed ? t('task.titleBar.saveFailed') : !isDirty ? t('task.titleBar.saved') : t('task.titleBar.save')}
          title={saveFailed ? t('task.titleBar.saveFailed') : undefined}
          className={`ml-3 flex h-8 items-center justify-center gap-1.5 rounded-md px-3 text-sm font-medium transition-colors disabled:pointer-events-none
                      ${isPhone && !isDirty && !saving ? '' : 'min-w-[110px]'}
                      ${saveFailed && !saving
                        ? 'bg-ws-error-fg/10 text-ws-error-fg hover:opacity-90'
                        : 'bg-ws-primary text-white hover:opacity-90 disabled:opacity-40'}`}
        >
          {!isDirty && !saving && <Check aria-hidden='true' className='h-4 w-4' />}
          {!(isPhone && !isDirty && !saving) && (
            <span aria-hidden='true'>
              {saving ? t('task.titleBar.saving') : saveFailed ? t('task.titleBar.saveFailed') : !isDirty ? t('task.titleBar.saved') : t('task.titleBar.save')}
            </span>
          )}
        </button>
        <div aria-live='polite' aria-atomic='true' className='sr-only'>
          {saving ? t('task.titleBar.saving') : saveFailed ? t('task.titleBar.saveFailed') : !isDirty ? t('task.titleBar.saved') : ''}
        </div>
      </div>

      {rightSlot && <div className='ml-auto flex shrink-0 items-center gap-1'>{rightSlot}</div>}
    </div>
  );
}
